import { useState } from 'react';
import { motion } from 'framer-motion';
import {
    TrendingUp, TrendingDown, Groups, AccessTime as Timer,
    ShowChart, Person, KeyboardArrowDown, KeyboardArrowUp,
} from '@mui/icons-material';
import { Container, Typography, Box, Stack, Collapse, Button } from '@mui/material';
import StockPicksGraph from '../components/StockPicksGraph';
import { Surface } from '../components/primitives';

const MotionSurface = motion(Surface);
const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0 },
};

const positiveFactors = [
    { icon: ShowChart, title: 'Higher purchase volume',    description: 'Large quantities of stock purchases positively impact ranking.' },
    { icon: Timer,     title: 'More frequent purchases',   description: 'Regular buying activity increases the stock\'s score.' },
    { icon: Groups,    title: 'Multiple buyers',           description: 'More individual buyers suggest broader interest and confidence.' },
    { icon: Person,    title: 'High buyer credibility',    description: 'Buyers with strong track records increase confidence in the purchase.' },
];

const negativeFactors = [
    { icon: ShowChart, title: 'Higher sale volume',        description: 'Large quantities of stock sales negatively impact ranking.' },
    { icon: Timer,     title: 'More frequent sales',       description: 'Regular selling activity decreases the stock\'s score.' },
    { icon: Groups,    title: 'Multiple sellers',          description: 'More individual sellers suggest declining confidence.' },
    { icon: Person,    title: 'High seller credibility',   description: 'Sellers with strong track records increase concern about the stock.' },
];

const FactorSection = ({ title, icon: Icon, iconColor, factors }) => (
    <MotionSurface variants={itemVariants} padding={0}>
        <Box sx={{ p: 3, borderBottom: t => `1px solid ${t.palette.custom.border.subtle}` }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Icon sx={{ color: iconColor, fontSize: 22 }} />
                <Typography variant="h3" sx={{ color: 'text.primary' }}>
                    {title}
                </Typography>
            </Stack>
        </Box>
        <Box sx={{ p: 3 }}>
            <Stack spacing={2.5}>
                {factors.map((item, i) => {
                    const FactorIcon = item.icon;
                    return (
                        <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Box sx={{
                                p: 1,
                                borderRadius: 1,
                                bgcolor: 'custom.surface.subtle',
                                border: t => `1px solid ${t.palette.custom.border.subtle}`,
                                color: 'text.secondary',
                                display: 'flex',
                            }}>
                                <FactorIcon sx={{ fontSize: 16 }} />
                            </Box>
                            <Box>
                                <Typography variant="body1" sx={{ color: 'text.primary', mb: 0.5, fontWeight: 500 }}>
                                    {item.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                                    {item.description}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    </MotionSurface>
);

const StockRankingsInfo = () => {
    const [graphExpanded, setGraphExpanded] = useState(false);

    return (
        <Container maxWidth="lg" sx={{ pt: 6, pb: 8 }}>
            <motion.div
                initial="hidden"
                animate="visible"
                variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
            >
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography variant="h1" sx={{ color: 'text.primary', mb: 1 }}>
                        How stock rankings are determined
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640, mx: 'auto' }}>
                        A scoring system that analyzes Congress members' trading activities to help you
                        make informed investment decisions.
                    </Typography>
                </Box>

                <Stack spacing={3}>
                    <FactorSection
                        title="Factors that increase ranking"
                        icon={TrendingUp}
                        iconColor="success.main"
                        factors={positiveFactors}
                    />

                    <FactorSection
                        title="Factors that decrease ranking"
                        icon={TrendingDown}
                        iconColor="error.main"
                        factors={negativeFactors}
                    />

                    {/* Historical performance (collapsible) */}
                    <MotionSurface variants={itemVariants} padding={0}>
                        <Box
                            sx={{
                                p: 3,
                                cursor: 'pointer',
                                transition: t => `background-color ${t.customTransitions.fast}`,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                            }}
                            onClick={() => setGraphExpanded(!graphExpanded)}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <TrendingUp sx={{ color: 'primary.light', fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="h4" sx={{ color: 'text.primary', mb: 0.25 }}>
                                            Historical track record
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            How "Very Bullish" picks have performed against the S&amp;P 500.
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Button
                                    variant="text"
                                    size="small"
                                    endIcon={graphExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                >
                                    {graphExpanded ? 'Hide' : 'View'}
                                </Button>
                            </Stack>
                        </Box>

                        <Collapse in={graphExpanded}>
                            <Box sx={{ p: 3, pt: 0 }}>
                                <StockPicksGraph />
                            </Box>
                        </Collapse>
                    </MotionSurface>

                    {/* Summary */}
                    <MotionSurface variants={itemVariants} padding={3.5}>
                        <Typography variant="h3" sx={{ color: 'text.primary', mb: 1.5 }}>
                            Summary
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, lineHeight: 1.7 }}>
                            The overall ranking of a stock is determined by balancing positive factors against
                            negative ones. This gives a comprehensive view of Congress members' trading activities —
                            both bullish and bearish sentiments — yielding clear insight into market dynamics.
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                            The system is designed to be transparent, so you can see exactly why a stock is ranked
                            higher or lower based on the latest Congressional disclosure data.
                        </Typography>
                    </MotionSurface>
                </Stack>
            </motion.div>
        </Container>
    );
};

export default StockRankingsInfo;
